import { useState, type FormEvent } from "react";
import { brand, channelGroups } from "@/mocks/homeData";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daij6tboh653ivfvoe60";

export default function SubscribeSection() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <section id="contact" className="w-full bg-background-100 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 font-label text-xs text-primary-600 tracking-widest">
              <i className="ri-mail-send-line" />
              CONTACT
            </span>
            <h2 className="mt-3 font-heading font-black text-2xl md:text-4xl text-foreground-950">
              订阅更新 · 联系我们
            </h2>
            <p className="mt-4 font-body text-sm md:text-base text-foreground-600 leading-relaxed">
              留下邮箱，我们会在每周挑选 3~5 条值得关注的热点与频道更新发给你。
              如果你有想推荐的话题或投稿线索，也可以直接发邮件给我们。
            </p>

            <div className="mt-6 space-y-3">
              <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-mail-line" />
                </span>
                <div>
                  <div className="font-label text-xs text-foreground-500">邮箱</div>
                  <a
                    href={`mailto:${brand.email}`}
                    className="font-label text-sm text-foreground-900 hover:text-primary-600 cursor-pointer"
                  >
                    {brand.email}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-time-line" />
                </span>
                <div>
                  <div className="font-label text-xs text-foreground-500">回复时间</div>
                  <div className="font-label text-sm text-foreground-900">{brand.workTime}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              id="subscribe-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="sub-name"
                    className="block font-label text-sm text-foreground-700 mb-2"
                  >
                    称呼
                  </label>
                  <input
                    id="sub-name"
                    name="nickname"
                    type="text"
                    maxLength={30}
                    placeholder="怎么称呼你"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-3 font-label text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label
                    htmlFor="sub-email"
                    className="block font-label text-sm text-foreground-700 mb-2"
                  >
                    邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="sub-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-3 font-label text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label
                  htmlFor="sub-channel"
                  className="block font-label text-sm text-foreground-700 mb-2"
                >
                  最感兴趣的板块
                </label>
                <select
                  id="sub-channel"
                  name="channel"
                  defaultValue=""
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-3 font-label text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 cursor-pointer"
                >
                  <option value="">请选择（可不填）</option>
                  {channelGroups.map((group) => (
                    <option key={group.id} value={group.title}>
                      {group.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-4">
                <label
                  htmlFor="sub-note"
                  className="block font-label text-sm text-foreground-700 mb-2"
                >
                  想说的话
                </label>
                <textarea
                  id="sub-note"
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="可以留下想推荐的话题或建议，不超过 500 字"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-3 font-label text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 resize-none"
                />
              </div>

              <div className="aux-field" aria-hidden="true">
                <label htmlFor="sub-alt">备用联系电话</label>
                <input
                  id="sub-alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="font-label text-xs text-foreground-500 max-w-xs">
                  提交即表示同意我们仅将邮箱用于更新提醒，不会用于其它用途。
                </p>
                <button
                  type="submit"
                  className="whitespace-nowrap cursor-pointer font-label text-sm px-6 py-3 rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
                >
                  提交订阅
                </button>
              </div>

              {status === "success" && (
                <div className="mt-5 flex items-center gap-2 rounded-md bg-secondary-50 border border-secondary-200 px-4 py-3 text-secondary-800">
                  <i className="ri-checkbox-circle-line text-lg" />
                  <span className="font-label text-sm">提交成功，感谢你的关注！我们会尽快处理。</span>
                </div>
              )}
              {status === "error" && (
                <div className="mt-5 flex items-center gap-2 rounded-md bg-primary-50 border border-primary-200 px-4 py-3 text-primary-700">
                  <i className="ri-error-warning-line text-lg" />
                  <span className="font-label text-sm">{formError}</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}